import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getOverview, listSignals, listReminders } from '../api/client';
import { SignalCard, ReminderCard, EmptyState } from '../components';
import type { Signal } from '../components/SignalCard';
import type { Reminder } from '../components/ReminderCard';

interface OverviewCounts {
  urgent_count: number;
  new_signals_count: number;
  pending_confirmations: number;
  today_reminders: number;
  items_requiring_review: number;
  // Business KPIs
  open_orders_count: number;
  in_progress_orders_count: number;
  orders_fulfilled_today: number;
  active_entities_count: number;
  overdue_reminders_count: number;
  signals_by_category: Record<string, number>;
}

interface StatCard {
  key: string;
  label: string;
  icon: string;
  count: number;
  accent?: boolean;
  link: string;
}

const CATEGORY_ICONS: Record<string, string> = {
  customer_order: '🛒',
  order_change: '✏️',
  delivery_update: '🚚',
  pickup_request: '📦',
  supplier_issue: '⚠️',
  inventory_shortage: '📉',
  production_task: '⚙️',
  roasting_request: '🔥',
  packaging_request: '📫',
  invoice_or_payment: '💰',
  followup_needed: '🔔',
  reminder: '📋',
  urgent_issue: '🚨',
};

export default function Overview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [counts, setCounts] = useState<OverviewCounts | null>(null);
  const [urgentSignals, setUrgentSignals] = useState<Signal[]>([]);
  const [recentSignals, setRecentSignals] = useState<Signal[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      try {
        const [overviewRes, urgentRes, recentRes, remindersRes] =
          await Promise.allSettled([
            getOverview(),
            listSignals({ urgency: 'critical,high', status: 'new', limit: 5 }),
            listSignals({ limit: 10 }),
            listReminders({ status: 'active' }),
          ]);

        if (cancelled) return;

        if (overviewRes.status === 'fulfilled') {
          setCounts(overviewRes.value.data);
        }
        if (urgentRes.status === 'fulfilled') {
          const data = urgentRes.value.data;
          setUrgentSignals(Array.isArray(data) ? data : data.items ?? []);
        }
        if (recentRes.status === 'fulfilled') {
          const data = recentRes.value.data;
          setRecentSignals(Array.isArray(data) ? data : data.items ?? []);
        }
        if (remindersRes.status === 'fulfilled') {
          const data = remindersRes.value.data;
          setReminders(Array.isArray(data) ? data : data.items ?? []);
        }

      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        fetchAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleConfirm = (id: string) => navigate(`/signals?highlight=${id}`);
  const handleDismiss = (id: string) => navigate(`/signals?highlight=${id}`);
  const handleExpand = (id: string) => navigate(`/signals?highlight=${id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  const stats: StatCard[] = [
    {
      key: 'urgent',
      label: t('overview.urgentItems'),
      icon: '🔴',
      count: counts?.urgent_count ?? urgentSignals.length,
      accent: (counts?.urgent_count ?? urgentSignals.length) > 0,
      link: '/signals?urgency=critical,high',
    },
    {
      key: 'new',
      label: t('overview.newSignals'),
      icon: '📡',
      count: counts?.new_signals_count ?? recentSignals.length,
      link: '/signals?status=new',
    },
    {
      key: 'pending',
      label: t('overview.pendingConfirmations'),
      icon: '⏳',
      count: counts?.pending_confirmations ?? 0,
      link: '/signals?status=awaiting_review',
    },
    {
      key: 'reminders',
      label: t('overview.todayReminders'),
      icon: '🔔',
      count: counts?.today_reminders ?? reminders.length,
      link: '/reminders',
    },
  ];

  const bizStats: StatCard[] = [
    {
      key: 'open_orders',
      label: t('overview.openOrders'),
      icon: '🛒',
      count: counts?.open_orders_count ?? 0,
      link: '/orders',
    },
    {
      key: 'orders_in_progress',
      label: t('overview.ordersInProgress'),
      icon: '⚙️',
      count: counts?.in_progress_orders_count ?? 0,
      link: '/orders',
    },
    {
      key: 'active_entities',
      label: t('overview.activeEntities'),
      icon: '📋',
      count: counts?.active_entities_count ?? 0,
      link: '/entities',
    },
    {
      key: 'overdue_reminders',
      label: t('overview.overdueReminders'),
      icon: '⚠️',
      count: counts?.overdue_reminders_count ?? 0,
      accent: (counts?.overdue_reminders_count ?? 0) > 0,
      link: '/reminders',
    },
  ];

  const hasAnyData =
    urgentSignals.length > 0 ||
    recentSignals.length > 0 ||
    reminders.length > 0;

  const categoryEntries = Object.entries(counts?.signals_by_category ?? {})
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);

  const hasOrdersData =
    (counts?.open_orders_count ?? 0) > 0 ||
    (counts?.in_progress_orders_count ?? 0) > 0 ||
    (counts?.orders_fulfilled_today ?? 0) > 0;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('overview.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('overview.recentChanges')}</p>
      </div>

      {/* Signals KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <button
            key={stat.key}
            onClick={() => navigate(stat.link)}
            className={`flex flex-col items-center gap-2 rounded-xl border p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${
              stat.accent
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span className="text-2xl">{stat.icon}</span>
            <span className={`text-3xl font-bold ${stat.accent ? 'text-red-600' : 'text-gray-900'}`}>
              {stat.count}
            </span>
            <span className="text-xs text-gray-500 text-center leading-tight">{stat.label}</span>
          </button>
        ))}
      </div>

      {/* Operations Pipeline KPI row */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          {t('overview.operationsPipeline')}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {bizStats.map((stat) => (
            <button
              key={stat.key}
              onClick={() => navigate(stat.link)}
              className={`flex flex-col items-center gap-2 rounded-xl border p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${
                stat.accent
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <span className="text-2xl">{stat.icon}</span>
              <span className={`text-3xl font-bold ${stat.accent ? 'text-orange-600' : 'text-gray-900'}`}>
                {stat.count}
              </span>
              <span className="text-xs text-gray-500 text-center leading-tight">{stat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Orders Pipeline summary */}
      {hasOrdersData && (
        <section className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <span>🛒</span> {t('overview.ordersPipeline')}
            </h2>
            <button
              onClick={() => navigate('/orders')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid grid-cols-3 divide-x divide-gray-100">
            <div className="pr-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{counts?.open_orders_count ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">{t('overview.openOrders')}</p>
            </div>
            <div className="px-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{counts?.in_progress_orders_count ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">{t('overview.ordersInProgress')}</p>
            </div>
            <div className="pl-4 text-center">
              <p className="text-2xl font-bold text-green-600">{counts?.orders_fulfilled_today ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">{t('overview.ordersFulfilledToday')}</p>
            </div>
          </div>
        </section>
      )}

      {/* Signals by Category */}
      {categoryEntries.length > 0 && (
        <section className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📊</span> {t('overview.signalsByCategory')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {categoryEntries.map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => navigate(`/signals?category=${cat}`)}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm hover:bg-gray-100 transition-colors"
              >
                <span>{CATEGORY_ICONS[cat] ?? '📌'}</span>
                <span className="text-gray-700">{t(`categories.${cat}`, { defaultValue: cat })}</span>
                <span className="ml-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!hasAnyData && (
        <EmptyState
          icon="✅"
          title={t('overview.noUrgentItems')}
          description={t('overview.allClear')}
        />
      )}

      {/* Urgent signals */}
      {urgentSignals.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>🔴</span> {t('overview.urgentItems')}
            </h2>
            <button
              onClick={() => navigate('/signals?urgency=critical,high')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {urgentSignals.slice(0, 5).map((signal) => (
              <SignalCard
                key={signal.id}
                signal={signal}
                compact
                onConfirm={handleConfirm}
                onDismiss={handleDismiss}
                onExpand={handleExpand}
              />
            ))}
          </div>
        </section>
      )}

      {/* Recent signals feed */}
      {recentSignals.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>📡</span> {t('overview.newSignals')}
            </h2>
            <button
              onClick={() => navigate('/signals')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recentSignals.slice(0, 10).map((signal) => (
              <SignalCard
                key={signal.id}
                signal={signal}
                onConfirm={handleConfirm}
                onDismiss={handleDismiss}
                onExpand={handleExpand}
              />
            ))}
          </div>
        </section>
      )}

      {/* Today's reminders */}
      {reminders.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>🔔</span> {t('overview.todayReminders')}
            </h2>
            <button
              onClick={() => navigate('/reminders')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {reminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                onSnooze={() => {}}
                onComplete={() => {}}
                onDismiss={() => {}}
                onReschedule={() => {}}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}
